import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChatKit, useChatKit } from '@openai/chatkit-react';
import { http } from '../lib/httpClient.js';

const ENV = typeof import.meta !== 'undefined' ? import.meta.env || {} : {};
const CHATKIT_ENABLED = String(ENV.VITE_CHATKIT_ENABLED ?? '0').trim() !== '0';
const CHATKIT_LABEL = (ENV.VITE_CHATKIT_LABEL || 'ClaimSupplement Copilot').trim();
const DEVICE_STORAGE_KEY = 'cv.chatkit.device';

function ensureDeviceId() {
  if (typeof window === 'undefined') {
    return `anon-${Date.now().toString(36)}`;
  }
  try {
    const cached = window.localStorage.getItem(DEVICE_STORAGE_KEY);
    if (cached) return cached;
    const generated =
      window.crypto?.randomUUID?.() ||
      `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(DEVICE_STORAGE_KEY, generated);
    return generated;
  } catch {
    return `anon-${Date.now().toString(36)}`;
  }
}

function captureMetadata() {
  if (typeof window === 'undefined') return undefined;
  const { hostname, pathname, search } = window.location || {};
  return {
    hostname: hostname || '',
    path: pathname || '',
    search: search || '',
  };
}

export default function ChatKitDock() {
  if (!CHATKIT_ENABLED) return null;
  return <ChatKitDockInner />;
}

function ChatKitDockInner() {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const deviceIdRef = useRef(null);
  const metadataRef = useRef(captureMetadata());

  if (!deviceIdRef.current) {
    deviceIdRef.current = ensureDeviceId();
  }

  useEffect(() => {
    if (!isOpen) {
      setStatus('idle');
      setError('');
    }
  }, [isOpen]);

  const theme = useMemo(
    () => ({
      colorScheme: 'light',
      radius: 'soft',
      color: {
        accent: {
          primary: '#2563eb',
          level: 2,
        },
      },
    }),
    [],
  );

  const requestClientSecret = useCallback(
    async (existingSecret) => {
      setError('');
      setStatus(existingSecret ? 'refreshing' : 'starting');

      const endpoint = existingSecret ? 'chatkit/refresh' : 'chatkit/session';
      try {
        const response = await http.post(endpoint, {
          json: {
            user: deviceIdRef.current,
            metadata: metadataRef.current,
            ...(existingSecret ? { clientSecret: existingSecret } : {}),
          },
        });

        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !payload?.client_secret) {
          const message = payload?.error || 'Chat assistant unavailable';
          throw new Error(message);
        }

        setStatus('ready');
        return payload.client_secret;
      } catch (err) {
        const message = err?.message || 'Chat assistant unavailable';
        setStatus('error');
        setError(message);
        throw err;
      }
    },
    [],
  );

  const { control } = useChatKit({
    api: {
      async getClientSecret(existing) {
        return requestClientSecret(existing);
      },
    },
    theme,
  });

  const toggleOpen = useCallback(() => {
    setIsOpen((value) => !value);
  }, []);

  const handleRetry = useCallback(() => {
    setError('');
    setStatus('idle');
    setIsOpen(false);
    setTimeout(() => setIsOpen(true), 100);
  }, []);

  const showOverlay = status === 'starting' || status === 'refreshing';

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 text-sm">
      {isOpen && (
        <div className="relative flex h-[520px] w-[360px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 text-xs uppercase tracking-wide text-slate-500">
            <span>{CHATKIT_LABEL}</span>
            <button
              type="button"
              onClick={toggleOpen}
              className="rounded-full px-2 py-1 text-[11px] font-semibold text-slate-500 transition hover:bg-slate-100"
            >
              Close
            </button>
          </div>
          <div className="relative flex-1 bg-slate-50">
            <ChatKit control={control} className="h-full w-full bg-white" />
            {showOverlay && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Connecting to assistant...
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/90 px-6 text-center">
                <p className="text-sm font-medium text-slate-700">{error}</p>
                <button
                  type="button"
                  onClick={handleRetry}
                  className="rounded-full bg-slate-900 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-white"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={toggleOpen}
        className="flex items-center gap-2 rounded-full bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
        aria-pressed={isOpen}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M5 8l2 2 4-4" />
          <path d="M5 15l2 2 4-4" />
          <path d="M15 10h6" />
          <path d="M15 14h6" />
          <path d="M7 19h10" />
          <path d="M7 5h10" />
        </svg>
        {isOpen ? 'Hide Copilot' : 'Open Copilot'}
      </button>
    </div>
  );
}
